import { create } from 'zustand';
import { setUser, clearUser } from '../telemetry';
import { record } from '../observability';
import { local } from '../lib/safeStorage';
import { signOutSince } from '../lib/signOuts';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: string[];
  photo_url?: string;
  gender?: string | null;
  birth_date?: string | null;
}

interface AuthStore {
  user: User | null;
  /** A SIGN-IN: the store accepts this user. Only the pages that sign a user
   *  in call it (`Login`, `AuthCallback`). A sign-in also removes the key of
   *  a sign-out that did not reach the server (#666). */
  setAuth: (user: User) => void;
  /** #666 — An answer about the signed-in user: the read at the page load, a
   *  saved profile, a role change. `stamp` is the value of `signOutStamp()`
   *  of `lib/signOuts.ts`. The code gets it before the request. The answer
   *  replaces the user, also when the id differs: the cookie session is the
   *  source of truth. The store ignores the answer when a sign-out runs now,
   *  or when a sign-out began or ended after the stamp. The result is true
   *  when the store accepts the answer. */
  refreshAuth: (user: User, stamp: number) => boolean;
  clearAuth: () => void;
  hasRole: (...roles: string[]) => boolean;
}

// Phase 2: token lives in HttpOnly cookies (lmf_access / lmf_refresh) set by
// the backend at login. The localStorage `user` blob is a first-render
// hydration optimisation only — the cookie session is the source of truth
// and App.tsx boots a getMe() validation on every page load.
// #457 — every storage call goes through `lib/safeStorage.ts`. When the
// browser blocks site data, a bare read throws at module load, before React
// mounts: a blank page, for guests too. A damaged blob throws in JSON.parse
// the same way. With the storage off, the cookie session still works.
function readStoredUser(): User | null {
  return local.getJson<User | null>('user', null);
}
function writeStoredUser(user: User | null): void {
  if (user) local.setJson('user', user);
  else local.remove('user');
}

// #666 — A sign-out that did not reach the server. Only the answer to the POST
// of a sign-out removes the HttpOnly cookies. `logout()` in `api/client.ts`
// sets this key before its first request and removes it after a 2xx answer.
// While the key is set, the browser can still hold the cookies of the
// session. The next page load then sends the POST again
// (`finishPendingSignOut()` in `api/client.ts`). A sign-in removes the key,
// because the cookies of the new session replace the old cookies. The value
// is the raw text '1'.
const SIGN_OUT_PENDING_KEY = 'lmf:signout-pending';

/** #666 — True when a sign-out did not reach the server. */
export function signOutPending(): boolean {
  return local.get(SIGN_OUT_PENDING_KEY) !== null;
}
/** #666 — `logout()` calls this before its first request. The result is
 *  false when the browser refuses the key (a blocked or full storage). Then
 *  the next page load cannot send the sign-out again, and the warning must
 *  not say that it does. */
export function markSignOutPending(): boolean {
  return local.set(SIGN_OUT_PENDING_KEY, '1');
}
/** #666 — A 2xx answer to the POST of a sign-out, or a sign-in. */
export function clearSignOutPending(): void {
  local.remove(SIGN_OUT_PENDING_KEY);
}

export const useAuthStore = create<AuthStore>((set, get) => {
  /** The store accepts `user`: `setAuth()` and a `refreshAuth()` that passes its guard. */
  function take(user: User): void {
    // #452 — `login` means a SIGN-IN. Before #452, the event fired on each
    // call: the page load (the getMe check), a profile save and a socket
    // refresh. It fires now when the identity changes: no user before, or a
    // different user. Since #666 those three places call refreshAuth(), which
    // calls take() when its guard accepts the answer.
    const signedIn = get().user?.id !== user.id;
    writeStoredUser(user);
    setUser(user.id, user.roles);
    if (signedIn) record({ event: 'login', props: { roles: user.roles?.join(',') || '' } });
    set({ user });
  }

  return {
    // #666 — A sign-out that did not reach the server: the user chose to sign
    // out on this device, so the first render shows no user. A tab that
    // closed during the sign-out keeps the stored user too. Before, the first
    // render showed that user, and the bell read the notices with the old
    // cookies before `finishPendingSignOut()` ran.
    user: signOutPending() ? null : readStoredUser(),
    setAuth: (user) => {
      // #666 — The sign-in set the cookies of a new session, so the next page
      // load must not send the sign-out again.
      clearSignOutPending();
      take(user);
    },
    // #666 — An answer can arrive after a sign-out. Before the fix, the read
    // at the page load called setAuth() for it and signed the user in again.
    // The bell then asked for the unread count with no cookie, and the
    // interceptor sent the user to /login?reason=session_expired. The user
    // signed out on purpose. The code finds such an answer with the stamp of
    // the read. An id check cannot: the answer to a stale user blob has
    // another id too, and that answer must replace the blob.
    refreshAuth: (user, stamp) => {
      if (signOutSince(stamp)) return false;
      take(user);
      return true;
    },
    clearAuth: () => {
      writeStoredUser(null);
      clearUser();
      set({ user: null });
    },
    hasRole: (...roles) => {
      const u = get().user;
      if (!u) return false;
      return roles.some(r => u.roles.includes(r));
    },
  };
});
